/**
 * Portrait rédigé le 2026-09-07 à partir de sources publiques vérifiées une à une
 * (registre des entreprises, Mission French Tech, French Tech BFC, sites des sociétés).
 * Daté au moment des faits : annonce des lauréats French Tech Tremplin Incubation du 3 juillet 2026.
 * Le champ `jsonLd` porte les données structurées Person / Organization (voir src/blog/README.md).
 */
export default {
  "slug": "mehdi-mougin-signon-french-tech-tremplin-besancon",
  "title": "Mehdi Mougin, le Bisontin qui veut faire traduire la langue des signes par l'IA",
  "excerpt": "Deux fois distingué par la French Tech en sept mois, le fondateur bisontin de SIGN ON développe un traducteur automatique de langue des signes française. Objectif : les moments où ne pas se comprendre coûte cher.",
  "seoTitle": "SIGN ON, traducteur IA de langue des signes",
  "seoDescription": "Portrait de Mehdi Mougin, fondateur de SIGN ON, lauréat French Tech Tremplin Incubation 2026 avec un traducteur automatique de langue des signes française.",
  "category": "startups",
  "tags": [
    "Mehdi Mougin",
    "SignOn",
    "French Tech",
    "Langue des signes",
    "Accessibilité"
  ],
  "author": "jimmy",
  "publishedAt": "2026-07-06",
  "updatedAt": "2026-07-06",
  "featured": false,
  "cover": {
    "image": "/img/blog/signon-logo-cover.png",
    "plain": true,
    "credit": "Logo : SIGN ON",
    "alt": "Logo de SIGN ON : le mot « SignOn » encadré par deux mains signant en langue des signes"
  },
  "content": [
    {
      "type": "lead",
      "text": "Arriver aux urgences et ne pas pouvoir décrire sa douleur. Entendre un diagnostic sans le comprendre. Pour les personnes sourdes, ces situations sont banales — et l'interprète en langue des signes n'est pas toujours disponible à trois heures du matin. C'est ce vide que veut combler **SIGN ON**, société bisontine fondée par **Mehdi Mougin**. Le 3 juillet 2026, la Mission French Tech l'a retenue parmi les 102 lauréats de la 5ᵉ promotion de son programme Tremplin Incubation, dévoilée à [VivaTech](/blog/vivatech-2026-percipio-robotics-startups-bfc)."
    },
    {
      "type": "keyfacts",
      "title": "En bref",
      "facts": [
        {
          "label": "Qui",
          "value": "Mehdi Mougin, ingénieur en intelligence artificielle, Besançon"
        },
        {
          "label": "Quoi",
          "value": "SIGN ON, SASU immatriculée le 12 février 2026 à Besançon"
        },
        {
          "label": "Le produit",
          "value": "Traduction en temps réel entre langue des signes française, oral et écrit"
        },
        {
          "label": "Distinctions",
          "value": "Coup de cœur du public French Tech Tremplin Prépa 2025, puis lauréat Tremplin Incubation 2026"
        },
        {
          "label": "Terrain visé",
          "value": "La santé : accueil hospitalier, consultation, téléconsultation"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Deux distinctions French Tech en sept mois"
    },
    {
      "type": "p",
      "text": "La première est venue de la région. Le 12 décembre 2025, au Village by CA de Dijon, La French Tech Bourgogne-Franche-Comté clôturait la promotion 2025 de son programme Tremplin Prépa. Au terme des pitchs, la salle a voté : « Coup de cœur du public : Mehdi Mougin pour SignOn ».[^1] Un prix décerné par une assemblée d'entrepreneurs et d'accompagnateurs, sur la seule force d'une présentation."
    },
    {
      "type": "p",
      "text": "La seconde est nationale. Le 3 juillet 2026, la Mission French Tech a publié la liste des 102 entreprises retenues pour la 5ᵉ promotion de **French Tech Tremplin Incubation**, dévoilée à VivaTech.[^2] SIGN ON y figure, rattachée à [la Capitale French Tech Bourgogne-Franche-Comté](/blog/french-tech-bfc-capitale-2026-2028-besancon), aux côtés de six autres lauréats régionaux : ARENIA, CENTAGA SAVEY, HILAJO GROUP, INTELLECTUALITY, MATERN'UP et MEND ME.[^3]"
    },
    {
      "type": "p",
      "text": "Le programme Tremplin n'est pas un concours de plus. Porté par la Mission French Tech et Bpifrance, il vise l'égalité des chances dans un secteur où l'accès au capital et aux réseaux reste très inégal. Concrètement, un lauréat Incubation obtient une année d'accompagnement par un incubateur partenaire, un soutien financier et l'accès à un réseau de mentors et d'investisseurs."
    },
    {
      "type": "p",
      "text": "À ces deux sélections s'ajoute le **prix du public des CIC Start Innovation Awards 2026**, catégorie que le concours de la banque a introduite cette année-là.[^9] SIGN ON en fait état sur son site.[^4]"
    },
    {
      "type": "h2",
      "text": "Une entreprise née d'un problème d'accès"
    },
    {
      "type": "p",
      "text": "SIGN ON n'est pas partie d'une prouesse technique en quête d'un marché, mais de l'inverse : un besoin identifié, puis les moyens de le traiter. Le premier terrain visé est la santé — accueil hospitalier, consultation, téléconsultation.[^4] Ce choix n'a rien d'anodin. C'est précisément là que l'incompréhension se paie le plus cher : un symptôme mal décrit, une posologie mal saisie, un consentement donné sans avoir tout compris."
    },
    {
      "type": "p",
      "text": "Le recours à un interprète professionnel reste la référence, mais il suppose d'être planifié. Or les moments où la communication compte le plus sont rarement planifiables : une urgence, une mauvaise nouvelle, une question qui surgit en fin de consultation. Entre l'interprète indisponible et le bout de papier griffonné, il n'existe aujourd'hui pas grand-chose."
    },
    {
      "type": "p",
      "text": "C'est cette zone-là que l'entreprise vise, et elle le dit sans emphase : son outil est présenté **en complément de l'interprète humain, et non en remplacement**.[^4] La nuance est décisive. Les communautés sourdes ont vu défiler des promesses technologiques qui prétendaient remplacer une profession tout en ignorant la complexité de la langue. Se positionner comme un recours d'appoint, disponible quand l'humain ne l'est pas, est à la fois plus juste et plus crédible."
    },
    {
      "type": "h2",
      "text": "Une langue à part entière"
    },
    {
      "type": "p",
      "text": "La langue des signes française n'est pas du français gesticulé : c'est une langue complète, avec sa grammaire, sa syntaxe spatiale et ses expressions du visage porteuses de sens. Un même geste change de valeur selon l'endroit où il est produit, la vitesse, le regard. C'est ce qui rend l'exercice exigeant — et ce qui sépare un travail sérieux des gadgets qui ont prétendu régler la question."
    },
    {
      "type": "p",
      "text": "L'offre se décline en trois formats : une application mobile et web, une intégration aux outils de visioconférence — Zoom, Teams, Meet — et des bornes d'accueil physiques.[^4] Trois réponses à trois situations où quelqu'un se retrouve, aujourd'hui, à devoir se débrouiller seul."
    },
    {
      "type": "h2",
      "text": "Un parcours court et dense"
    },
    {
      "type": "p",
      "text": "L'entreprise est jeune : SIGN ON a été immatriculée le 12 février 2026 à Besançon, avec Mehdi Mougin pour président.[^5] Le travail, lui, a commencé bien plus tôt."
    },
    {
      "type": "p",
      "text": "La trace publique la plus ancienne remonte à 2024 : un compte Hugging Face ouvert en février, puis dix-sept dépôts de code publiés sur GitHub entre janvier et décembre — traitement du langage naturel, réseaux de neurones convolutifs, reconnaissance d'entités nommées, agents conversationnels, mise en production de modèles.[^6] Un portfolio d'apprentissage méthodique, qui s'interrompt fin 2024, au moment où le travail devient privé."
    },
    {
      "type": "p",
      "text": "En juin 2025, il cofonde à Besançon l'association **[KOHAI](/blog/kohai-association-deep-tech-besancon)**, dédiée à l'accompagnement des projets deep tech et à la transmission des savoirs en IA.[^7] Entre le premier dépôt de code public et le statut de lauréat French Tech, il s'est écoulé un peu plus de deux ans."
    },
    {
      "type": "p",
      "text": "La même année, il est distingué dans le cadre de ses études par la **Société des membres de la Légion d'honneur**.[^8] Six mois plus tard vient le coup de cœur du public à Dijon, huit mois plus tard la sélection nationale."
    },
    {
      "type": "h2",
      "text": "Un second front : la vision industrielle"
    },
    {
      "type": "p",
      "text": "Mehdi Mougin mène en parallèle un second projet, **ANOMA**, sur un terrain très différent : le contrôle qualité industriel.[^8] L'offre repose sur la même brique technique — la vision par ordinateur — mais s'adresse aux usines : détection de micro-défauts sur ligne de production, décision conforme/non conforme, historisation horodatée de chaque contrôle avec son image, tableaux de bord qualité et pilotage multi-postes. Le site revendique des configurations tenant en environnement contraint, jusqu'à l'indice de protection IP67, et cite Besançon, Montbéliard et Pontarlier."
    },
    {
      "type": "p",
      "text": "Le rapprochement des deux projets n'a rien d'incohérent, il est même la marque d'une compétence transposable. Interpréter une main qui signe et repérer une rayure de quelques micromètres relèvent de la même famille de problèmes : extraire du sens d'un flux d'images, en temps réel, avec peu de données d'entraînement. C'est aussi, à Besançon, un terrain d'excellence : la ville a bâti sa réputation industrielle sur la précision et [les microtechniques](/blog/femto-st-besancon-capitale-microtechniques), et ANOMA vient s'y inscrire naturellement."
    },
    {
      "type": "h2",
      "text": "Ce que ce parcours dit de l'écosystème local"
    },
    {
      "type": "p",
      "text": "Le cas SIGN ON illustre une mécanique qui fonctionne : un programme régional repère un porteur de projet, la reconnaissance locale précède la sélection nationale, et l'accompagnement suit. C'est précisément ce que le label Capitale French Tech est censé produire dans un territoire qui n'a ni la densité de capital-risque de Paris ni celle de Lyon."
    },
    {
      "type": "p",
      "text": "Il illustre aussi une trajectoire que le territoire gagnerait à faire connaître : deux distinctions French Tech en sept mois, une société créée, un produit qui avance — le tout depuis Besançon, sans passer par Paris. L'écosystème bisontin sait faire émerger ce genre de projets ; il lui reste à les raconter."
    },
    {
      "type": "p",
      "text": "L'année d'accompagnement qui s'ouvre avec le programme Tremplin doit servir à cela : passer des premiers résultats aux premiers déploiements, et trouver les établissements prêts à ouvrir leurs portes. À Besançon, une entreprise qui met la précision au service de l'accès aux soins ne détonne pas — c'est même assez fidèle à ce que la ville sait faire depuis longtemps."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "Clap de fin pour la French Tech Tremplin Prépa 2025 : une promotion inspirante célébrée au Village by CA Dijon",
      "publisher": "La French Tech Bourgogne-Franche-Comté",
      "url": "https://lafrenchtechbfc.fr/actualites/programmes/clap-de-fin-pour-la-french-tech-tremplin-prepa-2025-une-promotion-inspirante-celebree-au-village-by-ca-dijon/",
      "date": "2025-12-12",
      "type": "official"
    },
    {
      "id": 2,
      "title": "Les 102 lauréats de la 5e promotion de French Tech Tremplin Incubation dévoilés à VivaTech",
      "publisher": "Mission French Tech",
      "url": "https://lafrenchtech.gouv.fr/fr/les-102-laureats-de-la-5e-promotion-de-french-tech-tremplin-incubation-devoiles-a-vivatech/",
      "date": "2026-07-03",
      "type": "official"
    },
    {
      "id": 3,
      "title": "Les entreprises lauréates de la 5e promotion de French Tech Tremplin Incubation et leurs Capitales ou Communautés French Tech de rattachement (PDF)",
      "publisher": "Mission French Tech",
      "url": "https://lafrenchtech.gouv.fr/app/uploads/2026/07/Les-entreprises-laureates-de-la-5e-promotion-de-French-Tech-Tremplin-Incubation-et-leurs-Capitales-ou-Communautes-French-Tech-de-rattachement.pdf",
      "date": "2026-07-03",
      "type": "official"
    },
    {
      "id": 4,
      "title": "Traduction de la langue des signes en temps réel",
      "publisher": "SIGN ON",
      "url": "https://signon-app.fr/traduction-langue-des-signes-temps-reel",
      "date": "2026-07-05",
      "type": "official"
    },
    {
      "id": 5,
      "title": "SIGN ON — fiche entreprise (SIREN 101 114 759)",
      "publisher": "Annuaire des entreprises, INSEE / RNE",
      "url": "https://annuaire-entreprises.data.gouv.fr/entreprise/101114759",
      "date": "2026-07-05",
      "type": "official"
    },
    {
      "id": 6,
      "title": "Profil de développeur MOUGINM",
      "publisher": "GitHub",
      "url": "https://github.com/MOUGINM",
      "date": "2026-07-05",
      "type": "web"
    },
    {
      "id": 7,
      "title": "L'association KOHAI",
      "publisher": "KOHAI",
      "url": "https://kohai.fr/association",
      "date": "2026-07-05",
      "type": "official"
    },
    {
      "id": 8,
      "title": "ANOMA — à propos",
      "publisher": "ANOMA",
      "url": "https://anoma.fr/about.html",
      "date": "2026-07-05",
      "type": "official"
    },
    {
      "id": 9,
      "title": "Sixième édition pour le concours CIC Start Innovation Awards",
      "publisher": "TEMIS Technopole Besançon",
      "url": "https://www.temis.org/sixieme-edition-pour-le-concours-cic-start-innovation-awards/",
      "date": "2026-01-29",
      "type": "press"
    }
  ],
  "jsonLd": [
    {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Mehdi Mougin",
      "givenName": "Mehdi",
      "familyName": "Mougin",
      "jobTitle": "Ingénieur en intelligence artificielle, fondateur de SIGN ON",
      "description": "Ingénieur en intelligence artificielle établi à Besançon. Fondateur et président de SIGN ON, qui développe un traducteur automatique de la langue des signes française, et porteur du projet de vision industrielle ANOMA. Lauréat French Tech Tremplin Incubation 2026 et coup de cœur du public French Tech Tremplin Prépa 2025.",
      "url": "https://www.ia-besancon.fr/blog/mehdi-mougin-signon-french-tech-tremplin-besancon",
      "image": "https://www.ia-besancon.fr/logo512.png",
      "knowsAbout": [
        "Intelligence artificielle",
        "Vision par ordinateur",
        "Traitement automatique du langage naturel",
        "Apprentissage profond",
        "Langue des signes française",
        "Vision industrielle"
      ],
      "homeLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Besançon",
          "addressRegion": "Bourgogne-Franche-Comté",
          "addressCountry": "FR"
        }
      },
      "sameAs": [
        "https://www.linkedin.com/in/mmdevia/",
        "https://github.com/MOUGINM",
        "https://huggingface.co/mehdiIA",
        "https://www.malt.fr/profile/mehdimougin",
        "https://signon-app.fr/",
        "https://anoma.fr/"
      ],
      "award": [
        "Lauréat French Tech Tremplin Incubation 2026 (5e promotion), Mission French Tech",
        "Coup de cœur du public, French Tech Tremplin Prépa 2025, La French Tech Bourgogne-Franche-Comté",
        "Prix du public, CIC Start Innovation Awards 2026",
        "Distinction de la Société des membres de la Légion d'honneur"
      ],
      "founder": [
        {
          "@type": "Organization",
          "name": "SIGN ON",
          "url": "https://signon-app.fr/"
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "SIGN ON",
      "alternateName": "SignOn",
      "url": "https://signon-app.fr/",
      "description": "Société bisontine développant une solution de traduction en temps réel entre la langue des signes française, l'oral et l'écrit : application mobile et web, intégration aux outils de visioconférence et bornes d'accueil. Elle vise en priorité les situations de santé — accueil hospitalier, consultation, téléconsultation — où l'absence d'interprète compromet la compréhension, et se positionne en complément de l'interprète humain.",
      "foundingDate": "2026-02-12",
      "legalName": "SIGN ON",
      "taxID": "101114759",
      "founder": {
        "@type": "Person",
        "name": "Mehdi Mougin"
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Besançon",
        "addressRegion": "Bourgogne-Franche-Comté",
        "addressCountry": "FR"
      },
      "award": [
        "Lauréat French Tech Tremplin Incubation 2026 (5e promotion)",
        "Coup de cœur du public, French Tech Tremplin Prépa 2025",
        "Prix du public, CIC Start Innovation Awards 2026"
      ],
      "sameAs": [
        "https://annuaire-entreprises.data.gouv.fr/entreprise/101114759"
      ],
      "slogan": "Rendre la communication possible quand elle compte le plus"
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "ANOMA",
      "url": "https://anoma.fr/",
      "description": "Offre de vision industrielle développée à Besançon : détection de micro-défauts par analyse d'image, historisation des contrôles et pilotage de la qualité en production.",
      "founder": {
        "@type": "Person",
        "name": "Mehdi Mougin"
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Besançon",
        "addressRegion": "Bourgogne-Franche-Comté",
        "addressCountry": "FR"
      }
    }
  ]
};
